import { type ScaleLinear } from "d3";
import { debounce } from "lodash-es";
import { PureComponent } from "react";
import { connect } from "react-redux";
import { notificationDismiss } from "widgets";

import { rangeEquals } from "@/app-utils/range.ts";
import { type IRasterSegment, RasterizationService } from "@/app-utils/rasterization-service.ts";
import {
  type BamSummary,
  type Contig,
  type CoverageRaster,
  type Organism,
  type ProjectFile,
  type Range,
  type Track,
} from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import {
  fetchNgsCoverageFeatures,
  getTrackId,
  type INgsCoverageRangeLimits,
  type ITrackData,
  loadBamFileSummary,
  validateBamFileAgainstRefGenomes,
} from "@/redux/modules/genome-browser.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { NGS_COVERAGE_TRACK_HEIGHT, TRACK_SUBTYPE } from "@/settings/settings.ts";

import { notify } from "../../../modules/notifications/notify.js";
import { NgsCoverageTrack } from "./NgsCoverageTrack.js";

export interface INgsCoverageTrackData extends ITrackData {
  ngsCoverage?: number[];
  ngsCoverageRangeLimits?: INgsCoverageRangeLimits;
  rasterParams?: {
    range: Range;
    canvasWidth: number; // canvas width, important criteria of the ngs coverage created on back end
  };
}

export const INITIAL_NGS_COVERAGE_TRACK_DATA: Partial<INgsCoverageTrackData> = {
  ngsCoverage: [],
};

interface INgsCoverageTrackLoaderOwnProps {
  track: Track; // track spec
  bamFileId: number;
  scale: ScaleLinear<number, number>;
  handleCustomize: () => void;
  visiblePileup: boolean;
}

interface INgsCoverageTrackLoaderAppStateProps {
  trackData: INgsCoverageTrackData;
  range: Range;
  contig: Contig;
  refGenome?: Organism;
  trackWidth: number;
  bamSummary: BamSummary;
}

interface INgsCoverageTrackLoaderDispatchProps {
  loadBamFile: (bamFileId) => Promise<BamSummary>;
  showWarning: (warningMessage: string, fileId: number) => void;
  validateBamFileAgainstRefGenomes: (bamFile: ProjectFile) => Promise<string>;

  fetchNgsCoverageFeatures: (
    track: Track,
    contig: Contig,
    range: Range,
    bamFileId: number,
    canvasWidth: number,
  ) => Promise<CoverageRaster>;
}

interface INgsCoverageTrackLoaderProps
  extends INgsCoverageTrackLoaderAppStateProps,
    INgsCoverageTrackLoaderDispatchProps,
    INgsCoverageTrackLoaderOwnProps {}

function mapStateToProps(
  state: IAppState,
  ownProps: INgsCoverageTrackLoaderOwnProps,
): INgsCoverageTrackLoaderAppStateProps {
  const trackId: string = getTrackId({
    ...ownProps.track,
    fileId: ownProps.bamFileId,
    trackSubtype: TRACK_SUBTYPE.NGS_COVERAGE,
  });

  return {
    range: state.genomeBrowser.range,
    contig: state.genomeBrowser.contig,
    trackData: state.genomeBrowser.trackDataMap[trackId] as INgsCoverageTrackData,
    refGenome: state.genomeBrowser.genome,
    trackWidth: state.genomeBrowser.layout.trackWidth,
    bamSummary: state.genomeBrowser.bamDatafiles[ownProps.bamFileId],
  };
}

function mapDispatchToProps(dispatch): INgsCoverageTrackLoaderDispatchProps {
  return {
    loadBamFile: (bamFileId) => dispatch(loadBamFileSummary(bamFileId)),
    validateBamFileAgainstRefGenomes: (bamFile: ProjectFile) => dispatch(validateBamFileAgainstRefGenomes(bamFile)),
    showWarning: (warningMessage: string, fileId: number) =>
      notify.warning(
        {
          title: selMessage({ id: "genomeViewer.ngs.coverageTrack.alignmentValidation.warningTitle" }),
          children: warningMessage,
        },
        { autoClose: false, toastId: fileId },
      ),
    fetchNgsCoverageFeatures: (track: Track, contig: Contig, range: Range, bamFileId: number, canvasWidth: number) =>
      dispatch(fetchNgsCoverageFeatures(track, contig, range, bamFileId, canvasWidth)),
  };
}

interface INgsCoverageTrackLoaderState {
  rasterData: IRasterSegment[];
  message: string;
}

class NgsCoverageTrackLoader extends PureComponent<INgsCoverageTrackLoaderProps, INgsCoverageTrackLoaderState> {
  private rasterizationService: RasterizationService;

  constructor(props: INgsCoverageTrackLoaderProps) {
    super(props);

    this.state = {
      rasterData: null,
      message: null,
    };
  }

  loadData = () => {
    const bamFile = this.props.bamSummary?.bamFile;
    if (bamFile && !bamFile.hidden && !bamFile.deleted) {
      if (this.props.trackWidth != undefined && this.props.trackWidth > 0) {
        this.setState({ message: null });
        this.props
          .fetchNgsCoverageFeatures(
            this.props.track,
            this.props.contig,
            this.props.range,
            this.props.bamFileId,
            this.props.trackWidth,
          )
          .then(() => {
            this.setState({ message: null });
          })
          .catch((error) => {
            this.setState({ message: error.message });
          });
      }
    }
  };

  debouncedLoadData = debounce(this.loadData, 300, { maxWait: 1000 });

  loadAndValidateBamFile = () => {
    void this.props.loadBamFile(this.props.bamFileId).then((bamSummary: BamSummary) => {
      const bamFile: ProjectFile = bamSummary.bamFile;
      if (bamFile?.hidden) {
        this.setState({ message: "This BAM file is not accessible to you." });
      } else if (bamFile?.deleted) {
        this.setState({ message: "This BAM file has been deleted." });
      } else {
        this.loadData();
        void this.props.validateBamFileAgainstRefGenomes(bamFile).then((warning: string) => {
          if (warning) {
            this.props.showWarning(`${bamFile.fileName}: ${warning}`, bamFile.id);
          }
        });
      }
    });
  };

  componentDidMount() {
    this.rasterizationService = new RasterizationService(this.props.scale);

    this.loadAndValidateBamFile();
  }

  componentDidUpdate(
    prevProps: Readonly<INgsCoverageTrackLoaderProps>,
    _prevState: Readonly<INgsCoverageTrackLoaderState>,
  ) {
    const rangeDiffers = !rangeEquals(prevProps.range, this.props.range);
    const contigDiffers: boolean = prevProps.contig?.id !== this.props.contig?.id;
    const scaleDiffers: boolean = prevProps.scale !== this.props.scale;
    const trackWidthDiffers: boolean = this.props.trackWidth !== prevProps.trackWidth;
    const refGenomeDiffers: boolean = this.props.refGenome?.id !== prevProps.refGenome?.id;
    const customizationDiffers = this.props.track?.trackCustomization !== prevProps.track?.trackCustomization;

    if (refGenomeDiffers) {
      this.loadAndValidateBamFile();
    }

    if (scaleDiffers) {
      this.rasterizationService = new RasterizationService(this.props.scale);
    }

    if (contigDiffers || customizationDiffers) {
      this.loadData();
    } else if (rangeDiffers || trackWidthDiffers) {
      this.debouncedLoadData();
    }

    const trackDataDiffers: boolean = this.props.trackData !== prevProps.trackData;
    // update the layout
    if (trackDataDiffers || rangeDiffers || trackWidthDiffers) {
      this.updateLayout();
    }
  }

  componentWillUnmount() {
    notificationDismiss(this.props.bamFileId);
  }

  updateLayout = () => {
    this.setState({
      rasterData: this.rasterizationService.rasterNgsCoverageFeatures(this.props.trackData, this.props.range),
    });
  };

  render() {
    return (
      <NgsCoverageTrack
        track={this.props.track}
        rasterData={this.state.rasterData}
        bamFileId={this.props.bamFileId}
        range={this.props.range}
        trackHeight={NGS_COVERAGE_TRACK_HEIGHT}
        handleCustomize={this.props.handleCustomize}
        visiblePileup={this.props.visiblePileup}
        message={this.state.message}
      />
    );
  }
}

const SmartNgsCoverageTrackLoader = connect<
  INgsCoverageTrackLoaderAppStateProps,
  INgsCoverageTrackLoaderDispatchProps,
  INgsCoverageTrackLoaderOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(NgsCoverageTrackLoader);

export { SmartNgsCoverageTrackLoader as NgsCoverageTrackLoader };
